import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import CloseButton from 'ext-ui/components/CloseButton/CloseButton'
import Processing from 'ext-ui/components/Processing/Processing'
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  CHARGEBEE_CHECKOUT_EVENT,
  ChargebeeEntryPoint,
  ChargebeeFlow,
} from 'src/hooks/useChargebeeCheckout'
import useChargebeeSdk from 'src/hooks/useChargebeeSdk'
import { useTranslate } from 'src/localization/useTranslate'

import style from './ChargebeeCheckoutModal.style'

export type ChargebeeCheckoutModalProps = {
  isOpen: boolean
  url: string
  site: string
  dismissible: boolean
  flow: ChargebeeFlow
  entryPoint: ChargebeeEntryPoint
  onSuccess: (hostedPageId: string) => void
  onClose: () => void
  /** Called on non-dismissible close to re-fetch a fresh hosted-page URL. */
  onRefreshUrl: (cb: (url: string) => void) => void
}

const CONTAINER_ID = 'cb-checkout-container'

export default function ChargebeeCheckoutModal(
  props: ChargebeeCheckoutModalProps,
): React.ReactElement {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const { status, cbInstance } = useChargebeeSdk({ site: props.site })

  const [currentUrl, setCurrentUrl] = useState(props.url)
  const [mountKey, setMountKey] = useState(0)

  const trackBase = useMemo(
    () => ({ flow: props.flow, entry_point: props.entryPoint }),
    [props.flow, props.entryPoint],
  )

  // Stash callbacks + dismissible in a ref so the mount effect doesn't
  // re-fire (and re-create the iframe) every time the parent re-renders.
  const handlers = useRef({
    onSuccess: props.onSuccess,
    onClose: props.onClose,
    onRefreshUrl: props.onRefreshUrl,
    dismissible: props.dismissible,
  })
  useLayoutEffect(() => {
    handlers.current = {
      onSuccess: props.onSuccess,
      onClose: props.onClose,
      onRefreshUrl: props.onRefreshUrl,
      dismissible: props.dismissible,
    }
  })

  useEffect(() => {
    if (status !== 'ready' || !cbInstance) return
    let cancelled = false
    void cbInstance
      .createCheckout({
        url: currentUrl,
        callbacks: {
          loaded: (): void => {
            analytics.track(CHARGEBEE_CHECKOUT_EVENT, {
              action: 'loaded',
              ...trackBase,
            })
          },
          success: (data): void => {
            // eslint-disable-next-line no-console
            console.log('chargebee checkout success callback data', data)
            const id = data?.id ?? ''
            analytics.track(CHARGEBEE_CHECKOUT_EVENT, {
              action: 'completed',
              hosted_page_id: id,
              ...trackBase,
            })
            handlers.current.onSuccess(id)
          },
          close: (): void => {
            if (handlers.current.dismissible) {
              analytics.track(CHARGEBEE_CHECKOUT_EVENT, {
                action: 'closed',
                ...trackBase,
              })
              handlers.current.onClose()
              return
            }
            handlers.current.onRefreshUrl((newUrl) => {
              setCurrentUrl(newUrl)
              setMountKey((k) => k + 1)
            })
          },
          error: (err): void => {
            analytics.track(CHARGEBEE_CHECKOUT_EVENT, {
              action: 'errored',
              error_type: 'checkout_error',
              ...trackBase,
            })
            // eslint-disable-next-line no-console
            console.error('Chargebee checkout error', err)
            // Close even when non-dismissible so the user isn't trapped
            // in a broken modal with no recovery path.
            handlers.current.onClose()
          },
          // Chargebee's resize callback reports iframe content height; we
          // let CSS size the host instead so the modal stays at a fixed
          // size and the iframe scrolls internally if needed.
          resize: (): void => undefined,
        },
      })
      .then((checkout) => {
        if (cancelled) return
        checkout.mount(`#${CONTAINER_ID}`)
      })
      .catch((err) => {
        if (cancelled) return
        analytics.track(CHARGEBEE_CHECKOUT_EVENT, {
          action: 'errored',
          error_type: 'create_checkout',
          ...trackBase,
        })
        // eslint-disable-next-line no-console
        console.error('Chargebee createCheckout failed', err)
        // Close so the user isn't trapped in an empty modal with no
        // recovery path — same pattern as the `error:` callback.
        handlers.current.onClose()
      })
    return () => {
      cancelled = true
    }
  }, [status, cbInstance, mountKey, currentUrl, analytics, trackBase])

  useEffect(() => {
    if (status === 'error') {
      analytics.track(CHARGEBEE_CHECKOUT_EVENT, {
        action: 'errored',
        error_type: 'sdk_load',
        ...trackBase,
      })
    }
  }, [status, analytics, trackBase])

  return (
    <BaseModal
      css={style}
      className="ChargebeeCheckoutModal"
      aria-label={translate('Checkout')}
      isOpen={props.isOpen}
      onClose={props.dismissible ? props.onClose : (): void => undefined}
      showCloseButton={false}
      variant="interactive"
    >
      {props.dismissible && (
        <CloseButton
          className="ChargebeeCheckoutModal-close"
          variant="dark-on-light"
          onClick={props.onClose}
        />
      )}
      {status === 'loading' && (
        <div className="ChargebeeCheckoutModal-state">
          <Processing />
          <span>{translate('Loading checkout…')}</span>
        </div>
      )}
      {status === 'error' && (
        <div className="ChargebeeCheckoutModal-state">
          <span>{translate("We couldn't load the checkout. Try again.")}</span>
          <Button onClick={(): void => window.location.reload()}>
            {translate('Retry')}
          </Button>
        </div>
      )}
      {status === 'ready' && (
        <div
          id={CONTAINER_ID}
          className="ChargebeeCheckoutModal-iframe-host"
          key={mountKey}
        />
      )}
    </BaseModal>
  )
}
